import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  VideoIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  ClockIcon,
  PencilIcon,
  SendIcon,
  TrashIcon,
  CheckCircle,
  MessageCircle,
  Timer,
  XIcon,
  AlertTriangle,
} from 'lucide-react';
import type { AppointmentWithClient } from '../../types/appointment';
import SendAppointmentMessageModal from './SendAppointmentMessageModal';
import { GoogleCalendarLinkService } from '../../../lib/services/google-calendar-link.service';
import ActionCard from '../common/ActionCard';
import EntityStatusBadge from '../common/EntityStatusBadge';

interface AppointmentListCardProps {
  appointment: AppointmentWithClient;
  onEdit: (appointment: AppointmentWithClient) => void;
  onDelete: (appointment: AppointmentWithClient) => void;
  onStatusChange: (
    appointment: AppointmentWithClient,
    status: 'completed' | 'cancelled' | 'no_show'
  ) => void;
  onWhatsAppSuccess?: (message: string) => void;
}

function formatDateTime(iso: string) {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return { dateLabel: iso, timeLabel: '' };
  }
  return {
    dateLabel: date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
    timeLabel: date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
}

function calculateDuration(starts_at: string, ends_at: string): string {
  const start = new Date(starts_at);
  const end = new Date(ends_at);
  const diffMs = end.getTime() - start.getTime();
  const diffMins = Math.round(diffMs / 60000);

  if (diffMins < 60) {
    return `${diffMins} min`;
  }
  const hours = Math.floor(diffMins / 60);
  const mins = diffMins % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function getMeetingTypeIcon(type: 'in_person' | 'video' | 'phone') {
  switch (type) {
    case 'video':
      return <VideoIcon className="w-4 h-4" />;
    case 'phone':
      return <PhoneIcon className="w-4 h-4" />;
    case 'in_person':
      return <MapPinIcon className="w-4 h-4" />;
  }
}

function getMeetingTypeLabel(type: 'in_person' | 'video' | 'phone') {
  switch (type) {
    case 'video':
      return 'Video Call';
    case 'phone':
      return 'Phone Call';
    case 'in_person':
      return 'In-Person';
  }
}

function getStatusColor(status: AppointmentWithClient['status']) {
  switch (status) {
    case 'scheduled':
      return 'bg-blue-100 text-blue-700';
    case 'completed':
      return 'bg-green-100 text-green-700';
    case 'cancelled':
      return 'bg-red-100 text-red-700';
    case 'no_show':
      return 'bg-orange-100 text-orange-700';
  }
}

function getStatusLabel(status: AppointmentWithClient['status']) {
  switch (status) {
    case 'scheduled':
      return 'Scheduled';
    case 'completed':
      return 'Completed';
    case 'cancelled':
      return 'Cancelled';
    case 'no_show':
      return 'No Show';
  }
}

function getClientStatusColor(status: AppointmentWithClient['client_status']) {
  switch (status) {
    case 'active':
      return 'bg-[#5B8E87] text-white';
    case 'lead':
      return 'bg-[#C9A870] text-white';
    case 'past':
      return 'bg-gray-400 text-white';
    case 'cold':
      return 'bg-gray-300 text-gray-700';
    default:
      return 'bg-gray-200 text-gray-600';
  }
}

function getClientStatusLabel(status: AppointmentWithClient['client_status']) {
  switch (status) {
    case 'active':
      return 'Active';
    case 'lead':
      return 'Lead';
    case 'past':
      return 'Past';
    case 'cold':
      return 'Cold';
    default:
      return 'Unknown';
  }
}

export function AppointmentListCard({
  appointment,
  onEdit,
  onDelete,
  onStatusChange,
  onWhatsAppSuccess,
}: AppointmentListCardProps) {
  const navigate = useNavigate();
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [whatsAppMessageType, setWhatsAppMessageType] = useState<'confirmation' | 'reminder'>(
    'confirmation'
  );

  const startTime = formatDateTime(appointment.starts_at);
  const duration = calculateDuration(appointment.starts_at, appointment.ends_at);
  const isScheduled = appointment.status === 'scheduled';
  const isCancelled = appointment.status === 'cancelled';
  const isCompleted = appointment.status === 'completed';
  const isNoShow = appointment.status === 'no_show';

  const handleSendWhatsApp = (type: 'confirmation' | 'reminder') => {
    setWhatsAppMessageType(type);
    setShowWhatsAppModal(true);
  };

  const handleWhatsAppSuccess = () => {
    const messageType = whatsAppMessageType === 'confirmation' ? 'Confirmation' : 'Reminder';
    if (onWhatsAppSuccess) {
      onWhatsAppSuccess(`${messageType} sent successfully!`);
    }
  };

  const handleAddToGoogleCalendar = () => {
    const calendarUrl = GoogleCalendarLinkService.generateAddToCalendarUrl(
      appointment,
      appointment.client_name,
      appointment.client_phone,
      appointment.client_status
    );
    window.open(calendarUrl, '_blank');
  };

  const handleCardClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on buttons or interactive elements
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('a') ||
      target.closest('[role="button"]')
    ) {
      return;
    }
    navigate(`/appointment/${appointment.id}`, {
      state: { from: 'schedule' },
    });
  };

  // Determine icon and colors based on meeting type and status
  const getMeetingIcon = () => {
    switch (appointment.meeting_type) {
      case 'video':
        return <VideoIcon className="w-5 h-5" />;
      case 'phone':
        return <PhoneIcon className="w-5 h-5" />;
      case 'in_person':
        return <MapPinIcon className="w-5 h-5" />;
    }
  };

  const getIconColors = () => {
    if (isCompleted) {
      return { bg: 'bg-[#5B8E87]/10', color: 'text-[#5B8E87]' };
    }
    if (isCancelled) {
      return { bg: 'bg-red-100', color: 'text-red-600' };
    }
    if (isNoShow) {
      return { bg: 'bg-orange-100', color: 'text-orange-600' };
    }
    // Scheduled - color by meeting type
    switch (appointment.meeting_type) {
      case 'video':
        return { bg: 'bg-blue-100', color: 'text-blue-600' };
      case 'phone':
        return { bg: 'bg-purple-100', color: 'text-purple-600' };
      case 'in_person':
        return { bg: 'bg-green-100', color: 'text-green-600' };
    }
  };

  const iconColors = getIconColors();

  // Map client status to ActionCard format
  const clientStatus =
    appointment.client_status === 'active'
      ? 'Active'
      : appointment.client_status === 'lead'
      ? 'Lead'
      : 'Past';

  // Build title content
  const titleContent = (
    <div>
      <div className="text-sm font-medium text-[#212121] mb-1">
        {appointment.title}
      </div>
      <div className="text-xs text-gray-600">
        {getMeetingTypeLabel(appointment.meeting_type)}
      </div>
      {isCancelled && appointment.cancellation_reason && (
        <div className="text-xs text-red-600 mt-1">
          Cancelled: {appointment.cancellation_reason}
        </div>
      )}
    </div>
  );

  // Build metadata
  const metadata = [
    { icon: <CalendarIcon className="w-3.5 h-3.5" />, text: startTime.dateLabel },
    { icon: <ClockIcon className="w-3.5 h-3.5" />, text: startTime.timeLabel },
    { icon: <Timer className="w-3.5 h-3.5" />, text: duration },
  ];

  // Status badge
  const statusBadge = (
    <EntityStatusBadge
      type="appointment"
      status={
        isScheduled
          ? 'Scheduled'
          : isCompleted
          ? 'Completed'
          : isCancelled
          ? 'Cancelled'
          : 'No Show'
      }
    />
  );

  // Card state for border
  const cardState = isCompleted ? 'completed' : isCancelled ? 'cancelled' : 'default';

  // Primary action
  const primaryAction = isScheduled
    ? {
        label: 'Mark Completed',
        icon: <CheckCircle className="w-4 h-4" />,
        onClick: () => onStatusChange(appointment, 'completed'),
        variant: 'success' as const,
      }
    : undefined;

  // Secondary actions
  const secondaryActions = [];
  if (isScheduled) {
    secondaryActions.push({
      icon: <CalendarIcon className="w-4 h-4" />,
      onClick: handleAddToGoogleCalendar,
      ariaLabel: 'Add to Google Calendar',
      variant: 'primary' as const,
    });
  }
  secondaryActions.push({
    icon: <MessageCircle className="w-4 h-4" />,
    onClick: () => navigate(`/chat/${appointment.client_id}`),
    ariaLabel: 'Open Chat',
    variant: 'default' as const,
  });

  // Menu items
  const menuItems = [
    {
      label: 'Edit',
      icon: <PencilIcon className="w-4 h-4" />,
      onClick: () => onEdit(appointment),
    },
  ];

  if (isScheduled) {
    menuItems.push(
      {
        label: 'Send Confirmation',
        icon: <SendIcon className="w-4 h-4" />,
        onClick: () => handleSendWhatsApp('confirmation'),
      },
      {
        label: 'Send Reminder',
        icon: <SendIcon className="w-4 h-4" />,
        onClick: () => handleSendWhatsApp('reminder'),
      },
      {
        label: 'Cancel Appointment',
        icon: <XIcon className="w-4 h-4" />,
        onClick: () => onStatusChange(appointment, 'cancelled'),
        variant: 'danger' as const,
      }
    );
  }

  if (!isScheduled && !isCancelled) {
    menuItems.push({
      label: 'Mark No Show',
      icon: <AlertTriangle className="w-4 h-4" />,
      onClick: () => onStatusChange(appointment, 'no_show'),
      variant: 'danger' as const,
    });
  }

  menuItems.push({
    label: 'Delete',
    icon: <TrashIcon className="w-4 h-4" />,
    onClick: () => onDelete(appointment),
    variant: 'danger' as const,
  });

  return (
    <>
      <div onClick={handleCardClick} className="cursor-pointer">
        <ActionCard
          icon={getMeetingIcon()}
          iconBgColor={iconColors.bg}
          iconColor={iconColors.color}
          clientName={appointment.client_name}
          clientStatus={clientStatus}
          clientId={appointment.client_id}
          title={titleContent}
          metadata={metadata}
          statusBadge={statusBadge}
          cardState={cardState}
          primaryAction={primaryAction}
          secondaryActions={secondaryActions}
          menuItems={menuItems}
          actionsLayout="inline"
        />
      </div>

      {/* WhatsApp Message Modal */}
      <SendAppointmentMessageModal
        isOpen={showWhatsAppModal}
        onClose={() => setShowWhatsAppModal(false)}
        onSuccess={handleWhatsAppSuccess}
        messageType={whatsAppMessageType}
        appointment={appointment}
        clientId={appointment.client_id}
      />
    </>
  );
}
